import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button, DatePicker, Form, Input, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, Spinner } from '../../../../common';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import { required } from '../../../../antd/form/FormValidation';
import PhdHelpHint from '../../../../common/Phd/PhdHelpHint.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import {
  useDeletePhdIndividualPlanTaskMutation,
  useGetPhdIndividualPlanTasksQuery,
  useSavePhdIndividualPlanTaskMutation,
} from '../../../../../redux/services/phd';

const PhdIndividualPlanTasksSection = ({
  planId,
  readOnly = false,
  value = [],
  onChange,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const isCreateMode = !planId;

  const {
    data: tasks,
    isFetching,
    isLoading: isLoadingTasks,
  } = useGetPhdIndividualPlanTasksQuery(
    planId ? { phd_individual_plan_id: planId } : undefined,
    { skip: !planId },
  );
  const [saveTask, { isLoading: isSaving }] =
    useSavePhdIndividualPlanTaskMutation();
  const [deleteTask, { isLoading: isDeleting }] =
    useDeletePhdIndividualPlanTaskMutation();
  const { saveSuccess, saveFailed, deleteSuccess, deleteFailed } =
    useToastMessages();

  const filteredTasks = useMemo(() => {
    if (isCreateMode) {
      return value;
    }

    return (tasks || []).filter(
      (task) => task?.phd_individual_plan_id === planId,
    );
  }, [isCreateMode, planId, tasks, value]);

  const openModal = (task) => {
    setEditingTask(task || null);
    setIsModalOpen(true);
    form.setFieldsValue({
      task: task?.task,
      date_deadline: task?.date_deadline ? dayjs(task.date_deadline) : null,
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTask(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const dateDeadline = values.date_deadline
      ? values.date_deadline.format('YYYY-MM-DD')
      : null;

    if (isCreateMode) {
      if (typeof onChange === 'function') {
        const item = {
          id: editingTask?.id ?? `new-${Date.now()}-${Math.random()}`,
          task: values.task,
          date_deadline: dateDeadline,
        };

        if (editingTask) {
          onChange(
            (value || []).map((row) =>
              row.id === editingTask.id ? item : row,
            ),
          );
        } else {
          onChange([...(value || []), item]);
        }
      }
      closeModal();
      return;
    }

    const payload = {
      task: values.task,
      phd_individual_plan_id: planId,
      date_deadline: dateDeadline,
    };

    try {
      await saveTask({ id: editingTask?.id, body: payload }).unwrap();
      saveSuccess();
      closeModal();
    } catch (error) {
      saveFailed(error);
    }
  };

  const handleDelete = async (row) => {
    if (isCreateMode) {
      if (typeof onChange === 'function') {
        onChange((value || []).filter((item) => item.id !== row.id));
      }
      return;
    }

    try {
      await deleteTask(row.id).unwrap();
      deleteSuccess();
    } catch (error) {
      deleteFailed(error);
    }
  };

  const taskFilter = useDataGridTextFilter('task');

  const columns = [
    {
      title: t('Úloha'),
      dataIndex: 'task',
      key: 'task',
      ellipsis: true,
      ...taskFilter,
    },
    {
      title: t('Termín'),
      dataIndex: 'date_deadline',
      key: 'date_deadline',
      render: (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '—'),
    },
  ];

  const modalTitle = editingTask ? t('Upraviť úlohu') : t('Pridať úlohu');

  return (
    <CollapseBlock
      collapsible
      title={
        <span>
          {t('Úlohy a termíny')}
          <PhdHelpHint
            label={t('Čo znamená termín úlohy plánu?')}
            title={t(
              'Termín úlohy je informačný a neovplyvňuje postup v doktorandskom štúdiu.',
            )}
          />
        </span>
      }
    >
      {isFetching && !isCreateMode ? (
        <Spinner />
      ) : (
        <>
          {!readOnly && (
            <div className="text-right" style={{ marginBottom: 12 }}>
              <Button type="primary" onClick={() => openModal(null)}>
                {t('Pridať úlohu')}
              </Button>
            </div>
          )}
          <DataGrid
            locale={{
              emptyText: t(
                'Pre zvolený pohľad a filtre nie sú dostupné žiadne záznamy.',
              ),
            }}
            rowKey={(task) => task.id}
            columns={columns}
            dataSource={filteredTasks}
            permissionsBasic="phd-individual-plan-tasks"
            uniqueTableId={`phd-individual-plan-tasks-${planId}`}
            onEditClick={readOnly ? undefined : openModal}
            onRowClick={readOnly ? undefined : openModal}
            onDelete={readOnly ? undefined : handleDelete}
            showActionText={!readOnly}
            disableSettings={readOnly}
            loading={
              isCreateMode ? false : isLoadingTasks || isSaving || isDeleting
            }
          />
        </>
      )}

      <Modal
        title={modalTitle}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        okButtonProps={{ loading: isSaving }}
        destroyOnClose
      >
        <Form layout="vertical" form={form} preserve={false}>
          <Form.Item name="task" label={t('Úloha')} rules={[required(t)]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="date_deadline"
            label={t('Termín')}
            rules={[required(t)]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </CollapseBlock>
  );
};

export default PhdIndividualPlanTasksSection;
